import type {
  Department,
  DepartmentCreate,
  DepartmentUpdate,
  Position,
  PositionCreate,
  PositionUpdate,
} from '@erp/shared';
import { Ban, Briefcase, Building2, Pencil, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Badge, Button, Card, Combobox, type ComboboxOption, confirmDialog, EmptyState, FormField, Input, Skeleton,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';

type Tab = 'departments' | 'positions';

export function OrganizationPage(): JSX.Element {
  const { t } = useTranslation('hr');
  const [tab, setTab] = useState<Tab>('departments');
  const [departments, setDepartments] = useState<Department[] | null>(null);
  const [positions, setPositions] = useState<Position[] | null>(null);

  const reload = async (): Promise<void> => {
    if (tab === 'departments') {
      const r = await api().get<{ items: Department[] }>('/hr/departments');
      setDepartments(r.data.items);
    } else {
      const r = await api().get<{ items: Position[] }>('/hr/positions');
      setPositions(r.data.items);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('org.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('org.description')}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        <TabButton active={tab === 'departments'} onClick={() => setTab('departments')}>
          {t('org.tabs.departments')}
        </TabButton>
        <TabButton active={tab === 'positions'} onClick={() => setTab('positions')}>
          {t('org.tabs.positions')}
        </TabButton>
      </div>

      {tab === 'departments' ? (
        <DepartmentsTab
          items={departments}
          onReload={async () => {
            await reload();
          }}
          parents={departments ?? []}
        />
      ) : (
        <PositionsTab
          items={positions}
          onReload={async () => {
            await reload();
          }}
        />
      )}
    </div>
  );
}

interface DeptTabProps {
  items: Department[] | null;
  parents: Department[];
  onReload: () => Promise<void>;
}

function DepartmentsTab({ items, parents, onReload }: DeptTabProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('hr.org.write');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Department | null>(null);

  if (!items) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const parentName = (id: string | null): string => {
    if (!id) return '—';
    return parents.find((p) => p.id === id)?.name ?? '—';
  };

  const openEdit = (d: Department): void => {
    setShowCreate(false);
    setEditing(d);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeactivate = async (d: Department): Promise<void> => {
    if (!(await confirmDialog({ message: t('org.dept.actions.confirmDeactivate', { code: d.code, name: d.name }), tone: 'danger' }))) return;
    try {
      await api().delete(`/hr/departments/${d.id}`);
      await onReload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (d: Department): Promise<void> => {
    if (!(await confirmDialog({ message: t('org.dept.actions.confirmReactivate', { code: d.code, name: d.name }) }))) return;
    try {
      // DepartmentUpdateSchema accepts isActive; PATCH brings the row
      // back. The DELETE endpoint flips isActive=false; this is the
      // mirror direction.
      await api().patch(`/hr/departments/${d.id}`, { isActive: true });
      await onReload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('org.dept.create.title')}</Button>
        )}
      </div>
      {showCreate && (
        <CreateDeptForm
          parents={parents}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await onReload();
          }}
        />
      )}
      {editing && (
        <EditDeptForm
          dept={editing}
          parents={parents}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onReload();
          }}
        />
      )}
      {items.length === 0 && !showCreate && !editing ? (
        <EmptyState
          icon={<Building2 className="h-8 w-8" aria-hidden />}
          title={t('org.dept.empty')}
          description={t('org.dept.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('org.dept.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-fg-subtle">
              <tr>
                <th className="px-4 py-2 text-start">{t('org.dept.columns.code')}</th>
                <th className="px-4 py-2 text-start">{t('org.dept.columns.name')}</th>
                <th className="px-4 py-2 text-start">{t('org.dept.columns.parent')}</th>
                <th className="px-4 py-2 text-start">{t('org.dept.columns.status')}</th>
                {canWrite && <th className="w-32 px-4 py-2 text-end" />}
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{d.code}</td>
                  <td className="px-4 py-2">{d.name}</td>
                  <td className="px-4 py-2 text-fg-muted">{parentName(d.parentId)}</td>
                  <td className="px-4 py-2">
                    <Badge tone={d.isActive ? 'success' : 'neutral'}>
                      {t(`org.dept.status.${d.isActive ? 'active' : 'inactive'}`)}
                    </Badge>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-end">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(d)}
                          aria-label={`${t('org.dept.actions.edit')} ${d.code}`}
                          title={t('org.dept.actions.edit')}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        {d.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeactivate(d)}
                            aria-label={`${t('org.dept.actions.deactivate')} ${d.code}`}
                            title={t('org.dept.actions.deactivate')}
                          >
                            <Ban className="h-4 w-4" aria-hidden />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onReactivate(d)}
                            aria-label={`${t('org.dept.actions.reactivate')} ${d.code}`}
                            title={t('org.dept.actions.reactivate')}
                          >
                            <RotateCcw className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

interface PositionsTabProps {
  items: Position[] | null;
  onReload: () => Promise<void>;
}

function PositionsTab({ items, onReload }: PositionsTabProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const canWrite = usePermission('hr.org.write');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Position | null>(null);

  if (!items) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const openEdit = (p: Position): void => {
    setShowCreate(false);
    setEditing(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDeactivate = async (p: Position): Promise<void> => {
    if (!(await confirmDialog({ message: t('org.pos.actions.confirmDeactivate', { code: p.code, title: p.title }), tone: 'danger' }))) return;
    try {
      await api().delete(`/hr/positions/${p.id}`);
      await onReload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const onReactivate = async (p: Position): Promise<void> => {
    if (!(await confirmDialog({ message: t('org.pos.actions.confirmReactivate', { code: p.code, title: p.title }) }))) return;
    try {
      // PositionUpdateSchema accepts isActive; PATCH brings the row back
      // — DELETE flips it to false, this is the mirror direction.
      await api().patch(`/hr/positions/${p.id}`, { isActive: true });
      await onReload();
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {canWrite && !showCreate && !editing && (
          <Button onClick={() => setShowCreate(true)}>{t('org.pos.create.title')}</Button>
        )}
      </div>
      {showCreate && (
        <CreatePosForm
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await onReload();
          }}
        />
      )}
      {editing && (
        <EditPosForm
          position={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await onReload();
          }}
        />
      )}
      {items.length === 0 && !showCreate && !editing ? (
        <EmptyState
          icon={<Briefcase className="h-8 w-8" aria-hidden />}
          title={t('org.pos.empty')}
          description={t('org.pos.emptyDescription')}
          action={
            canWrite ? (
              <Button size="sm" onClick={() => setShowCreate(true)}>
                {t('org.pos.create.title')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-fg-subtle">
              <tr>
                <th className="px-4 py-2 text-start">{t('org.pos.columns.code')}</th>
                <th className="px-4 py-2 text-start">{t('org.pos.columns.title')}</th>
                <th className="px-4 py-2 text-start">{t('org.pos.columns.payGrade')}</th>
                <th className="px-4 py-2 text-start">{t('org.pos.columns.status')}</th>
                {canWrite && <th className="w-32 px-4 py-2 text-end" />}
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-2">{p.title}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.payGrade ?? '—'}</td>
                  <td className="px-4 py-2">
                    <Badge tone={p.isActive ? 'success' : 'neutral'}>
                      {t(`org.dept.status.${p.isActive ? 'active' : 'inactive'}`)}
                    </Badge>
                  </td>
                  {canWrite && (
                    <td className="px-4 py-2 text-end">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(p)}
                          aria-label={`${t('org.pos.actions.edit')} ${p.code}`}
                          title={t('org.pos.actions.edit')}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        {p.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeactivate(p)}
                            aria-label={`${t('org.pos.actions.deactivate')} ${p.code}`}
                            title={t('org.pos.actions.deactivate')}
                          >
                            <Ban className="h-4 w-4" aria-hidden />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onReactivate(p)}
                            aria-label={`${t('org.pos.actions.reactivate')} ${p.code}`}
                            title={t('org.pos.actions.reactivate')}
                          >
                            <RotateCcw className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

interface CreateDeptProps {
  parents: Department[];
  onCancel: () => void;
  onCreated: () => Promise<void>;
}

function CreateDeptForm({ parents, onCancel, onCreated }: CreateDeptProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<DepartmentCreate>({ code: '', name: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/hr/departments', form);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{t('org.dept.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="d-code" label={t('org.dept.create.code')} required>
          <Input id="d-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="d-name" label={t('org.dept.create.name')} required>
          <Input id="d-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </FormField>
        <FormField id="d-parent" label={t('org.dept.create.parent')}>
          <Combobox
            id="d-parent"
            options={[{ value: '__none__', label: '—' }, ...parents.map((p): ComboboxOption => ({ value: p.id, label: p.name, keywords: p.name }))]}
            value={form.parentId ?? undefined}
            onChange={(v) => setForm({ ...form, parentId: v && v !== '__none__' ? v : null })}
            placeholder={t('org.dept.create.parent')}
            ariaLabel={t('org.dept.create.parent')}
          />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditDeptProps {
  dept: Department;
  parents: Department[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

function EditDeptForm({ dept, parents, onCancel, onSaved }: EditDeptProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  // Mirror CreateDeptForm's fields. Code is the unique identifier;
  // DepartmentUpdateSchema technically accepts it (it's CreateSchema.partial())
  // but renaming a code post-creation invites confusion in journal entries
  // and references — keep it read-only here, same shape items/parties use.
  // The parent dropdown filters out the dept being edited so a department
  // can't be made its own parent.
  const [form, setForm] = useState<DepartmentUpdate>({
    name: dept.name,
    parentId: dept.parentId,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: DepartmentUpdate = {
        name: form.name?.trim() || undefined,
        parentId: form.parentId ?? null,
      };
      await api().patch(`/hr/departments/${dept.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('org.dept.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">
        {t('org.dept.edit.title', { code: dept.code, name: dept.name })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="de-code" label={t('org.dept.create.code')} required>
          <Input
            id="de-code"
            value={dept.code}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="de-name" label={t('org.dept.create.name')} required>
          <Input
            id="de-name"
            value={form.name ?? ''}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="de-parent" label={t('org.dept.create.parent')}>
          <Combobox
            id="de-parent"
            options={[{ value: '__none__', label: '—' }, ...parents.filter((p) => p.id !== dept.id).map((p): ComboboxOption => ({ value: p.id, label: p.name, keywords: p.name }))]}
            value={form.parentId ?? undefined}
            onChange={(v) => setForm({ ...form, parentId: v && v !== '__none__' ? v : null })}
            placeholder={t('org.dept.create.parent')}
            ariaLabel={t('org.dept.create.parent')}
          />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('org.dept.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('org.dept.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface CreatePosProps {
  onCancel: () => void;
  onCreated: () => Promise<void>;
}

function CreatePosForm({ onCancel, onCreated }: CreatePosProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  const [form, setForm] = useState<PositionCreate>({ code: '', title: '' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api().post('/hr/positions', form);
      await onCreated();
    } catch {
      setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">{t('org.pos.create.title')}</h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="p-code" label={t('org.pos.create.code')} required>
          <Input id="p-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required autoFocus />
        </FormField>
        <FormField id="p-title" label={t('org.pos.create.jobTitle')} required>
          <Input id="p-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
        </FormField>
        <FormField id="p-grade" label={t('org.pos.create.payGrade')}>
          <Input id="p-grade" value={form.payGrade ?? ''} onChange={(e) => setForm({ ...form, payGrade: e.target.value })} />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{tc('actions.create')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>{tc('actions.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface EditPosProps {
  position: Position;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}

function EditPosForm({ position, onCancel, onSaved }: EditPosProps): JSX.Element {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation();
  // Mirror CreatePosForm's fields. Code stays read-only — it's the
  // unique identifier referenced by employees' positionId. Title +
  // payGrade are the editable bits. description isn't surfaced in
  // create either, keeping parity.
  const [form, setForm] = useState<PositionUpdate>({
    title: position.title,
    payGrade: position.payGrade,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const patch: PositionUpdate = {
        title: form.title?.trim() || undefined,
        // Empty payGrade collapses to null so the server clears the column.
        payGrade: form.payGrade && form.payGrade.trim() ? form.payGrade.trim() : null,
      };
      await api().patch(`/hr/positions/${position.id}`, patch);
      await onSaved();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(code === 'unprocessable' ? t('org.pos.errors.unprocessable') : tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2 className="mb-4 text-lg font-semibold">
        {t('org.pos.edit.title', { code: position.code, title: position.title })}
      </h2>
      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 md:grid-cols-2" noValidate>
        <FormField id="pe-code" label={t('org.pos.create.code')} required>
          <Input
            id="pe-code"
            value={position.code}
            readOnly
            aria-readonly
            className="cursor-not-allowed bg-bg-muted text-fg-muted"
          />
        </FormField>
        <FormField id="pe-title" label={t('org.pos.create.jobTitle')} required>
          <Input
            id="pe-title"
            value={form.title ?? ''}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            autoFocus
          />
        </FormField>
        <FormField id="pe-grade" label={t('org.pos.create.payGrade')}>
          <Input
            id="pe-grade"
            value={form.payGrade ?? ''}
            onChange={(e) => setForm({ ...form, payGrade: e.target.value })}
          />
        </FormField>
        {error && <p className="md:col-span-2 text-sm text-danger" role="alert">{error}</p>}
        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" loading={saving}>{t('org.pos.edit.submit')}</Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            {t('org.pos.edit.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps): JSX.Element {
  const cls = active
    ? 'px-4 py-2 rounded-md bg-bg-subtle text-fg border border-border text-sm font-medium'
    : 'px-4 py-2 rounded-md text-fg-muted hover:bg-bg-subtle text-sm';
  return <button type="button" onClick={onClick} className={cls}>{children}</button>;
}
